/**
 * CetusEd Admin Console — Email Provider Configuration
 *
 * COMPLIANCE: FERPA §1232g(b) — Email delivery of PII requires TLS 1.2+
 *   encryption. The email provider configuration must verify that the
 *   sending domain has proper SPF/DKIM/DMARC records configured.
 *
 *   IDEA §300.503 — Prior Written Notice (PWN) delivery via email requires
 *   proof of delivery. The email provider must support delivery tracking.
 */

import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useEmailConfig } from '../../api/hooks';

export const EmailConfig: React.FC = () => {
  const [districtId, setDistrictId] = useState('');
  const { data: config, isLoading, error } = useEmailConfig(districtId);

  const handleDistrictSelect = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setDistrictId(e.target.value);
  }, []);

  if (isLoading) return <div className="admin-loading">Loading email config...</div>;

  return (
    <div className="admin-page">
      <nav className="breadcrumb">
        <Link to="/integrations">Integrations</Link> / Email
      </nav>

      <div className="page-header">
        <h1>Email Configuration</h1>
      </div>

      <div className="form-field">
        <label htmlFor="email-district">Select District</label>
        <select id="email-district" value={districtId} onChange={handleDistrictSelect}>
          <option value="">Select a district...</option>
        </select>
      </div>

      {districtId && config && (
        <div className="detail-card">
          <h3>Email Provider</h3>
          <dl>
            <dt>Provider</dt>
            <dd>{config.provider || 'Not configured'}</dd>
            <dt>Status</dt>
            <dd><span className={`status-badge status-badge--${config.enabled ? 'active' : 'deactivated'}`}>
              {config.enabled ? 'Enabled' : 'Disabled'}
            </span></dd>
            <dt>From Address</dt>
            <dd>{config.fromAddress || '—'}</dd>
            <dt>Verified</dt>
            <dd>{config.verified ? 'Yes' : 'No — verify SPF/DKIM/DMARC records'}</dd>
          </dl>
        </div>
      )}

      {districtId && error && (
        <div className="admin-error" role="alert">
          Unable to load email configuration.
        </div>
      )}
    </div>
  );
};
